import {Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild} from "@angular/core";
import {Constants} from "@app/constants/constants";
import {NotificationService} from "@app/services/notification.service";
import {MethodsHelper} from "@app/helpers/methods.helper";
import {Branch} from "@app/models/branch";
import {BranchService} from "@app/services/branch.service";
import {Collateral} from "@app/models/collateral";
import {CollateralsService} from "@app/services/collaterals.service";
import {AuthenticationService} from "@app/services/authentication.service";
import {DatePipe} from "@angular/common";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: 'app-collateral-adjustment-popup',
    templateUrl: './collateral-adjustment-popup.component.html',
    styleUrls: ['./collateral-adjustment-popup.component.css'],
    standalone: false
})

export class CollateralAdjustmentPopupComponent implements OnInit, OnChanges {

    @Input() displayDialog: boolean;
    @Input() collateralAgreement: Collateral;
    @Output() onSubmit = new EventEmitter();
    @Output() onCancel = new EventEmitter();
    @Output() adjustmentReload = new EventEmitter();

    @ViewChild('form') form: any;

    collateralAdjustment: any;

    submittedForm: boolean = false;

    validationMessages: object;

    pageIndex = 0;
    pageSize = Constants.PAGE_ITEM_COUNT;
    totalElements: number;

    defaultPageParams = {
        size: 10,
        page: 0
    }

    yearRange: string;

    branchCodeList: Array<Branch>;


    constructor(
        private notificationService: NotificationService,
        private branchService: BranchService,
        private collateralsService: CollateralsService,
        private authenticationService: AuthenticationService,
        private datePipe: DatePipe,
        private translate: TranslateService
    ) {
    }

    ngOnInit() {
        if (!this.collateralAdjustment) {
            this.collateralAdjustment = new Object();
            this.collateralAdjustment.documentNumber = MethodsHelper.randomString(10);
            this.collateralAdjustment.collateralAgreementNumber = this.collateralAgreement.collateralAgreementNumber;
            this.collateralAdjustment.date = this.datePipe.transform(localStorage.getItem('businessDay') ? new Date(localStorage.getItem('businessDay')) : new Date(), 'dd-MM-yyyy');
            this.collateralAdjustment.adjustmentAmount = 0;
            this.collateralAdjustment.adjustmentQuantity = 0;
            this.collateralAdjustment.channelCode = Constants.CHANNEL.CHANNEL_CODE;
            this.collateralAdjustment.currency = this.collateralAgreement.currency;
            this.collateralAdjustment.description = this.collateralAgreement.description;
            this.collateralAdjustment.initiatedUserId = this.authenticationService.getCurrentUserId();
            this.collateralAdjustment.branchId = this.collateralAgreement.branchId;
            this.collateralAdjustment.customerSpecificOptionsDtos = this.collateralAgreement.customerSpecificOptions;
            this.collateralAdjustment.purpose = this.collateralAgreement.collateralAgreementNumber + ' ' + 'Collateral adjustment';
        }
        this.validationMessages = new Object();
        this.yearRange = MethodsHelper.getYearRange(0);
        this.getBranches();
    }

    ngOnChanges() {
        this.getAdjustmentInfo();
    }

    getAdjustmentInfo() {
        this.collateralsService.getAdjustmentInfo(this.collateralAgreement.id).subscribe({
            next: (data) => {
                this.collateralAdjustment.documentNumber = data.documentNumber;
                this.collateralAdjustment.collateralAgreementNumber = data.agreementNumber;
                this.collateralAdjustment.date = data.date;
                this.collateralAdjustment.currency = data.currency;
                this.collateralAdjustment.branchId = data.branchId;
                this.collateralAdjustment.purpose = data.purpose;
            },
            error: (err) => {
                let msg = MethodsHelper.showErrorMessageStyling(err);
                this.notificationService.showError("<div class='row'><p class='col-1 d-flex align-items-center'><i class='fa fa-times-circle'></i></p><p class='col'> " + `${msg}` + "</p></div>",
                    "");
            }
        });
    }

    getBranches() {
        const pageOption = {
            size: 10000,
            page: 0
        }
        const pageParams = { ...this.defaultPageParams, ...pageOption };
        this.branchService.getList(pageParams).subscribe(data => {
            this.branchCodeList = data.content;
            this.branchCodeList.forEach(code => {
                this.collateralAdjustment.branchId = JSON.parse(this.authenticationService.getUserRoles())[0]?.userBranchId;
            })
        });
    }

    onSave() {
        this.submittedForm = true;
        if (this.validateForm()) {
            this.collateralsService.adjust(this.collateralAgreement.id, this.collateralAdjustment).subscribe({
                next: (data) => {
                    this.notificationService.showHTMLMessage("<i class='fa fa-check-circle' style='line-height: 2'></i> <p>" + `Successfully adjusted.` + "</p>", "");
                    this.cancel();
                    this.adjustmentReload.emit();
                },
                error: (err) => {
                    let msg = MethodsHelper.showErrorMessageStyling(err);
                    this.notificationService.showError("<div class='row'><p class='col-1 d-flex align-items-center'><i class='fa fa-times-circle'></i></p><p class='col'> " + `${msg}` + "</p></div>",
                        "");
                }
            });
        } else {
            this.notificationService.showErrorMessage(this.translate.instant('error.something_incorrect'))}
    }

    validateForm() {
        for (let key in this.validationMessages) {
            if (this.validationMessages[key]) {
                return false;
            }
        }
        if (!this.form.value.quantity && !this.form.value.amount) {
            return false;
        }
        if (this.form.form) {
            return this.form.form.valid
        }
        return false;
    }

    cancel() {
        this.onCancel.emit();
    }

    changeBranch(value) {
        if (this.branchCodeList) {
            for (const element of this.branchCodeList) {
                if (element.id == value) {
                    this.collateralAdjustment.branchId = element.id;
                    break;
                }
            }
        }
    }

    numberOnly(event): boolean {
        const charCode = (event.which) ? event.which : event.keyCode;
        return !(charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57))
    }

    isSaveDisabled() {
        return (!this.collateralAdjustment.adjustmentAmount || Number(this.collateralAdjustment.adjustmentAmount) == 0) &&
            (!this.collateralAdjustment.adjustmentQuantity || Number(this.collateralAdjustment.adjustmentQuantity) == 0);
    }

}
